import { useEffect, useRef, useState } from "react";
import { Sparkles, Camera, FilePlus2, X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export async function uploadCustomerFiles(customerId, files, docs = []) {
  for (const f of files) {
    const fd = new FormData();
    fd.append("file", f); fd.append("category", "cliente_documento"); fd.append("entity_type", "customer"); fd.append("entity_id", customerId);
    fd.append("notes", docs.find((d) => d.filename === f.name)?.label || "Documento cliente");
    await api.post("/documents", fd);
  }
}

function Thumb({ file }) {
  const [u, setU] = useState(null);
  useEffect(() => { const x = URL.createObjectURL(file); setU(x); return () => URL.revokeObjectURL(x); }, [file]);
  return <img src={u || undefined} alt="" className="w-10 h-7 rounded object-cover bg-zinc-900" />;
}

function Picker({ onAdd, capture, testid, children }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" multiple={!capture} capture={capture} accept={capture ? "image/*" : "image/jpeg,image/png,image/webp,application/pdf"} className="hidden"
        data-testid={`${testid}-input`} onChange={(e) => { onAdd([...e.target.files]); e.target.value = ""; }} />
      <Button type="button" variant="outline" onClick={() => ref.current.click()} data-testid={testid}>{children}</Button>
    </>
  );
}

export default function AiCustomerFill({ onApply, label = "Compila con AI", testid = "ai-fill-btn", endpoint = "/ai/customer-extract", title = "Compila anagrafica con AI",
  description = "Carica una o più foto o PDF (carta d'identità fronte/retro, codice fiscale, patente). I dati letti compilano il modulo cliente: li controlli e poi salvi." }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const add = (fs) => { setFiles((s) => [...s, ...fs.map((f) => (f.name && f.name !== "image.jpg" ? f : new File([f], `foto_${Date.now()}_${s.length + 1}.jpg`, { type: f.type || "image/jpeg" })))].slice(0, 10)); setRes(null); };
  const close = () => { setOpen(false); setFiles([]); setRes(null); };
  const read = async () => {
    if (!files.length) { toast.error("Carica almeno una foto o un PDF"); return; }
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const r = await api.post(endpoint, fd);
      if (!Object.keys(r.data.fields).length) { setRes(r.data); return; }
      if (r.data.warnings.length || (r.data.tax_code_check && !r.data.tax_code_check.valid)) { setRes(r.data); return; }
      apply(r.data);
    } catch (e) { setRes({ error: errMsg(e) }); } finally { setBusy(false); }
  };
  const apply = (data) => { onApply(data.fields, files, data.documents); toast.success("Dati letti: controllali e salva"); close(); };
  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} data-testid={testid}><Sparkles size={16} className="mr-1" />{label}</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-xl" data-testid="ai-fill-dialog">
          <DialogHeader><DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-2">
            <Picker onAdd={add} testid="ai-fill-files"><FilePlus2 size={15} className="mr-1" />Scegli foto o file</Picker>
            <Picker onAdd={add} capture="environment" testid="ai-fill-camera"><Camera size={15} className="mr-1" />{files.length ? "Scatta un'altra foto" : "Scatta foto"}</Picker>
          </div>
          <p className="text-[11px] text-zinc-500">Puoi fare più scatti di seguito: fronte, retro e altri documenti. Poi premi "Leggi documenti".</p>
          <div className="space-y-1.5" data-testid="ai-fill-list">
            {files.length === 0 && <div className="text-sm text-zinc-500 py-5 text-center border border-dashed border-zinc-800 rounded-lg">Nessun file selezionato (max 10)</div>}
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-zinc-800">
                {f.type.startsWith("image/") ? <Thumb file={f} /> : <span className="w-10 h-7 rounded bg-zinc-900 grid place-items-center text-[10px] text-zinc-500">PDF</span>}
                <span className="flex-1 truncate">{i + 1}. {f.name}</span>
                <span className="text-xs text-zinc-500">{res?.documents?.find((d) => d.filename === f.name)?.label}</span>
                <button type="button" onClick={() => { setFiles((s) => s.filter((_, j) => j !== i)); setRes(null); }} className="text-zinc-500 hover:text-white" data-testid={`ai-fill-remove-${i}`}><X size={14} /></button>
              </div>
            ))}
          </div>
          {res && (
            <div className="text-sm rounded-md border border-amber-500/30 bg-amber-500/10 p-3 space-y-1" data-testid="ai-fill-warnings">
              {res.error && <div className="flex gap-2"><AlertTriangle size={15} className="shrink-0 mt-0.5" />{res.error}</div>}
              {(res.warnings || []).map((w) => <div key={w}>• {w}</div>)}
              {res.tax_code_check && !res.tax_code_check.valid && <div>• {res.tax_code_check.message}</div>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={close} data-testid="ai-fill-cancel">Annulla</Button>
            {res?.fields && Object.keys(res.fields).length > 0
              ? <Button type="button" className="btn-brand" onClick={() => apply(res)} data-testid="ai-fill-apply">Usa comunque i dati letti</Button>
              : <Button type="button" className="btn-brand" disabled={busy} onClick={read} data-testid="ai-fill-read">{busy ? <><Loader2 size={14} className="animate-spin mr-1" />Lettura in corso…</> : "Leggi documenti"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
